import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { useAuth } from '../contexts/AuthContext'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'
import { getEstimateExamples, EstimateExample } from '../api/client'
import { SummaryCard } from './EstimatePage'

// Public example gallery: admin-featured estimate cards (PRD-IdeaEstimator 3.3).
export default function EstimateExamples() {
  const { isAuthenticated } = useAuth()
  const [examples, setExamples] = useState<EstimateExample[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  usePageMeta(pageMeta('/estimate/examples'))

  useEffect(() => {
    getEstimateExamples()
      .then(setExamples)
      .catch(() => setError('The example gallery could not be loaded.'))
  }, [])

  return (
    <div className="min-h-screen flex flex-col">
      {!isAuthenticated && <PublicNavBar />}

      <div className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8">
          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
            Idea Estimator
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1">
            See what others are building
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            A hand-picked set of real estimates — new products, migrations, cloud exits, refactors — showing how the
            estimator scores complexity, picks a stack, and breaks the work down.
          </p>
          <Link
            to="/estimate"
            className="inline-block mt-4 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
          >
            Estimate your own idea &rarr;
          </Link>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        {!error && examples === null && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {examples && examples.length === 0 && (
          <p className="text-sm text-gray-400">No examples have been featured yet — check back soon.</p>
        )}

        {examples && examples.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {examples.map((ex) =>
              ex.summary ? (
                <div key={ex.id} data-testid="example-card">
                  <SummaryCard summary={ex.summary} estimateId={ex.id} />
                </div>
              ) : null,
            )}
          </div>
        )}
      </div>

      {!isAuthenticated && <PublicFooter />}
    </div>
  )
}
